/**
 * Theme — `110-design-system.md` §2.1.
 *
 * `ui.theme` is "system" | "light" | "dark". The <html> element always carries a
 * RESOLVED `data-theme="light" | "dark"` — never "system". The pre-paint script in
 * index.html stamps it before first paint; this module owns it from mount onwards.
 *
 * The part that is easy to get wrong: `system` must follow the OS **at runtime**.
 * A `matchMedia` read on boot is not enough — flipping the OS theme with the app
 * open must re-resolve without a reload. Hence the `change` listener.
 *
 * localStorage, not the server, is the read path: the value must be readable
 * synchronously before paint, which rules out both TanStack Query and IndexedDB
 * (`111-frontend-architecture.md` §5). It holds no credentials — it is 5 bytes of
 * per-device UI trivia. From M7 it is also mirrored to /api/settings so that a new
 * browser inherits it; localStorage stays the fast path.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type Theme = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'rn.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

interface ThemeContextValue {
  /** What the user chose. May be "system". */
  theme: Theme;
  /** What is actually on screen. Never "system". */
  resolved: ResolvedTheme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
}

function resolve(theme: Theme): ResolvedTheme {
  if (theme !== 'system') return theme;
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readStoredTheme()));

  // Stamp the resolved value on <html>. Dark rules key off [data-theme="dark"] only.
  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
  }, [resolved]);

  // AC #9: "the `system` setting follows the OS at runtime (flip the OS theme and
  // the app follows without a reload)". This listener is the whole criterion.
  useEffect(() => {
    if (theme !== 'system') {
      setResolved(theme);
      return;
    }

    const media = window.matchMedia(DARK_QUERY);
    const apply = () => {
      setResolved(media.matches ? 'dark' : 'light');
    };

    apply();
    media.addEventListener('change', apply);
    return () => {
      media.removeEventListener('change', apply);
    };
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    localStorage.setItem(STORAGE_KEY, next);
    setThemeState(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolved, setTheme }),
    [theme, resolved, setTheme],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === null) throw new Error('useTheme must be used inside <ThemeProvider>');
  return context;
}
